import * as React from 'react'
import format from 'date-fns/format'

import * as R from 'ramda'

import {
  Field,
  FieldWide,
  FieldHeader,
  FieldValue,
  FieldBigValue,
  FieldInput,
  FieldBigInput,
  TextAreaInput
} from './Styles'

import themeObj from '../../theme/defaultTheme'
import CustomSelect from '../Shared/CustomSelect'

export type FormHelperSelectOptions = {
  id: string
  displayName: string
}

export type FormHelperInputProps = {
  setState: (state: object) => void
  editMode?: boolean
  fieldNames?: object
}

type FormOption = {
  displayName: string
  id: string
}

export default class ProjectFormHelper {
  setState: (state: object) => void
  editMode: boolean
  fieldNames: object

  constructor(props: FormHelperInputProps) {
    this.setState = props.setState
    this.editMode = props.editMode || false
    this.fieldNames = props.fieldNames || {}
  }

  toggleEditMode(editMode: boolean) {
    this.editMode = editMode
  }

  textChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const changedField = {
      [event.currentTarget.name]: event.currentTarget.value
    }
    this.setState(changedField)
    event.preventDefault()
  }

  textAreaChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const changedField = {
      [event.currentTarget.name]: event.currentTarget.value
    }
    this.setState(changedField)
    event.preventDefault()
  }

  selectChanged = (
    event: React.ChangeEvent<HTMLSelectElement>,
    options: FormHelperSelectOptions[]
  ) => {
    const found = R.find(opt => opt.id === event.currentTarget.value, options)
    const changedField = {
      [event.currentTarget.name]: found ? found.id : null
    }
    // debug console.log('edit', event.currentTarget.name, event.currentTarget.value, found, changedField)
    this.setState(changedField)
    event.preventDefault()
  }

  _handleWidth = (width: string | null) => {
    return width ? { width } : {}
  }

  _handleFieldWidth = (width: string | null) => {
    return width ? { width: width } : {}
  }

  renderFieldHeader(name: string, width: string | null) {
    return (
      <FieldHeader style={this._handleWidth(width)}>
        {this.fieldNames[name] || ''}
      </FieldHeader>
    )
  }

  renderEditField(
    name: string,
    value: string,
    width: string | null,
    required: boolean = false,
    big: boolean = false,
    autoFocus: boolean = false,
    error?: boolean
  ) {
    const className =
      (required && value.length === 0) || error ? 'errorField' : ''
    return (
      <Field>
        {this.renderFieldHeader(name, width)}
        {this.editMode ? (
          big ? (
            <FieldBigInput
              name={name}
              onChange={this.textChanged}
              className={className}
              type="text"
              placeholder=""
              required={required}
              style={this._handleWidth(width)}
              value={value}
              autoFocus={autoFocus}
            />
          ) : (
            <FieldInput
              name={name}
              onChange={this.textChanged}
              className={className}
              type="text"
              placeholder=""
              required={required}
              style={this._handleWidth(width)}
              value={value}
              autoFocus={autoFocus}
            />
          )
        ) : big ? (
          <FieldBigValue style={this._handleWidth(width)}>
            {value}
          </FieldBigValue>
        ) : (
          <FieldValue style={this._handleWidth(width)}>{value}</FieldValue>
        )}
      </Field>
    )
  }

  parseOptions(options: FormOption[]) {
    const pOptions = options.map(op => (
      <option value={op.id} key={op.id}>
        {op.displayName}
      </option>
    ))
    pOptions.unshift(
      <option disabled={true} value={''} key={-1}>
        {' '}
        -- select --{' '}
      </option>
    )
    return pOptions
  }

  renderSelectField(
    name: string,
    value: string,
    options: FormHelperSelectOptions[],
    width: string | null,
    required: boolean | undefined = false
  ) {
    const selectedIndx = R.findIndex(opt => opt.id === value, options)
    const error = selectedIndx < 0
    const bColor = error ? themeObj.colors.error : themeObj.colors.light

    // debug console.log('render %s="%s" -> %d ', name, value, selectedIndx, error)
    return (
      <Field style={this._handleFieldWidth(width)}>
        {this.renderFieldHeader(name, width)}
        {this.editMode ? (
          <CustomSelect
            options={this.parseOptions(options)}
            currentVal={error ? value : options[selectedIndx].id}
            handleEventChange={event => this.selectChanged(event, options)}
            name={name}
            title={required ? 'Select value' : ''}
            required={required}
            style={{ borderColor: bColor, width: '100%' }}
          />
        ) : (
          <FieldValue style={this._handleWidth(width)}>
            {error ? value : options[selectedIndx].displayName}
          </FieldValue>
        )}
      </Field>
    )
  }

  renderEditTextArea(
    name: string,
    value: string,
    width: string | null,
    required: boolean | undefined = false
  ) {
    const className = required && value.length === 0 ? 'errorField' : ''
    return (
      <FieldWide>
        {this.renderFieldHeader(name, width)}
        {this.editMode ? (
          <TextAreaInput
            name={name}
            onChange={this.textAreaChanged}
            className={className}
            placeholder=""
            title={required ? 'Required data' : ''}
            readOnly={!this.editMode}
            style={this._handleWidth(width)}
            value={value}
          />
        ) : (
          <FieldValue style={this._handleWidth(width)}>{value}</FieldValue>
        )}
      </FieldWide>
    )
  }

  renderTextField(name: string, value: string, width: string | null) {
    return (
      <Field>
        {this.renderFieldHeader(name, width)}
        <FieldValue style={this._handleWidth(width)}>{value}</FieldValue>
      </Field>
    )
  }

  renderDateField(name: string, value: string, width: string | null) {
    const dateStr = format(value, 'MMMM, DD, YYYY')
    return (
      <Field>
        {this.renderFieldHeader(name, width)}
        <FieldValue style={this._handleWidth(width)}>{dateStr}</FieldValue>
      </Field>
    )
  }
}



// WEBPACK FOOTER //
// ./src/Components/Projects/ProjectFormHelper.tsx